import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, Moon, Bell, LogOut, Shield, Building, Clock, LogIn, LogOut as LogOutIcon, MessageCircle, Users, ArrowLeftRight, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import NotificationCenter from './NotificationCenter';

// Notification sounds using Web Audio API
const playNotificationSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const playNote = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0, ctx.currentTime + start);
      gain.gain.linearRampToValueAtTime(0.3, ctx.currentTime + start + 0.02);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + start + dur);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur);
    };
    playNote(784, 0, 0.12);
    playNote(1047, 0.1, 0.18);
  } catch (e) {
    console.log('Could not play sound');
  }
};

// Request browser notification permission
const requestNotificationPermission = async () => {
  if ('Notification' in window && Notification.permission === 'default') {
    await Notification.requestPermission();
  }
};

// Show browser notification (works even when tab is not active)
const showBrowserNotification = (title, body, onClick) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    const notification = new Notification(title, {
      body: body,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: 'ping-message-' + Date.now(),
      requireInteraction: false,
      silent: true, // We play our own sound
    });
    
    notification.onclick = () => {
      window.focus();
      if (onClick) onClick();
      notification.close();
    };
    
    // Auto close after 5 seconds
    setTimeout(() => notification.close(), 5000);
  }
};

const TopBar = ({ theme, onThemeToggle, currentUser, currentOffice }) => {
  const { logout, isSuperAdmin, isEmployee, isOfficeAdmin, canSwitchView, switchView, activeView, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [onlineTeam, setOnlineTeam] = useState([]);
  const [showOnlinePopover, setShowOnlinePopover] = useState(false);
  const onlinePopoverRef = useRef(null);
  const notifiedMessagesRef = useRef(new Set());
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch currently online team members in caller's office
  const fetchOnlineTeam = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/attendance/currently-online`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setOnlineTeam(data.online || []);
      }
    } catch (e) {
      // silent — header polling shouldn't disrupt user
    }
  }, [API_URL, getAuthHeaders]);

  useEffect(() => {
    fetchOnlineTeam();
    const interval = setInterval(fetchOnlineTeam, 60000); // poll every 60s
    return () => clearInterval(interval);
  }, [fetchOnlineTeam]);

  // Close popover on outside click
  useEffect(() => {
    if (!showOnlinePopover) return;
    const onDocClick = (e) => {
      if (onlinePopoverRef.current && !onlinePopoverRef.current.contains(e.target)) {
        setShowOnlinePopover(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showOnlinePopover]);

  // Request notification permission on mount
  useEffect(() => {
    requestNotificationPermission();
  }, []);

  // Show toast notification for new message
  const showToastNotification = useCallback((senderName, message) => {
    toast(
      <div 
        className="flex items-center gap-3 cursor-pointer" 
        onClick={() => navigate('/ping')}
      >
        <div 
          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium shrink-0"
          style={{ backgroundColor: '#25D366' }}
        >
          {senderName?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'P'}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-gray-900">{senderName || 'New Message'}</p>
          <p className="text-xs text-gray-500 truncate">{message}</p>
        </div>
      </div>,
      {
        duration: 5000,
        position: 'top-right',
        style: {
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
      }
    );
  }, [navigate]);

  // Trigger notification for a new message
  const triggerNotification = useCallback((senderName, message) => {
    // Always play sound (works in foreground, may be blocked in background by browser)
    playNotificationSound();
    
    // Always show browser notification for reliability (works even when tab is hidden)
    // Browser notifications are more reliable for background tabs
    showBrowserNotification(
      `Ping from ${senderName}`,
      message,
      () => navigate('/ping')
    );
    
    // Show in-app toast only when tab is visible (avoid duplicate notifications)
    if (!document.hidden) {
      showToastNotification(senderName, message);
    }
  }, [showToastNotification, navigate]);

  // Fetch attendance status on mount
  useEffect(() => {
    fetchAttendanceStatus();
  }, []);

  // Fetch unread count and check for new messages
  useEffect(() => {
    // Use refs for values that change frequently to avoid recreating the effect
    const checkForNewMessages = async () => {
      try {
        const headers = getAuthHeaders();
        if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
        
        // Fetch conversations to check for new messages
        const response = await fetch(`${API_URL}/api/chat/conversations`, { headers });
        if (!response.ok) return;
        
        const conversations = await response.json();
        
        // Calculate total unread
        const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);
        setUnreadCount(totalUnread);
        
        // Check each conversation for new messages
        conversations.forEach(conv => {
          if (!conv.last_message || !conv.last_message_time) return;
          
          // Create a unique key using conversation ID and message timestamp
          const messageKey = `${conv.id}:${conv.last_message_time}`;
          
          // Skip if we've already notified this exact message
          if (notifiedMessagesRef.current.has(messageKey)) return;
          
          // Skip on first load (don't notify for existing messages)
          if (!isInitialized) return;
          
          // Only notify if there are unread messages (means it's from someone else)
          if (conv.unread_count > 0) {
            const senderName = conv.is_group 
              ? (conv.last_message_sender || conv.group_name)
              : conv.other_participant_name;
            
            // Trigger notification
            triggerNotification(senderName, conv.last_message);
            
            // Mark as notified
            notifiedMessagesRef.current.add(messageKey);
            
            // Prevent memory leak - keep only recent message keys
            if (notifiedMessagesRef.current.size > 100) {
              const arr = Array.from(notifiedMessagesRef.current);
              notifiedMessagesRef.current = new Set(arr.slice(-50));
            }
          }
        });
        
        // Mark as initialized after first successful load
        if (!isInitialized) {
          // Initialize with current message keys so we don't notify on page load
          conversations.forEach(conv => {
            if (conv.last_message_time) {
              notifiedMessagesRef.current.add(`${conv.id}:${conv.last_message_time}`);
            }
          });
          setIsInitialized(true);
        }
        
      } catch (error) {
        console.error('Error checking for new messages:', error);
      }
    };

    // Initial check
    checkForNewMessages();
    
    // Poll every 2.5 seconds for more responsive notifications
    const interval = setInterval(checkForNewMessages, 2500);
    
    return () => clearInterval(interval);
  }, [getAuthHeaders, currentOffice, API_URL, isInitialized, triggerNotification]);

  const fetchAttendanceStatus = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/attendance/my-status`, { headers });
      if (response.ok) {
        const data = await response.json();
        setAttendanceStatus(data);
      }
    } catch (error) {
      console.error('Error fetching attendance status:', error);
    }
  };

  const handleCheckIn = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/attendance/check-in`, {
        method: 'POST',
        headers,
        body: JSON.stringify({})
      });
      if (response.ok) {
        const data = await response.json();
        fetchAttendanceStatus();

        // Show check-in toast with early/late info
        const isLate = data.is_late;
        const lateMin = data.late_minutes || 0;
        const now = new Date();
        const expectedHour = 8;
        const expectedMinute = 30;
        const expectedTotal = expectedHour * 60 + expectedMinute;
        const currentTotal = now.getHours() * 60 + now.getMinutes();
        const diffMin = currentTotal - expectedTotal;

        let timeMsg;
        if (isLate) {
          const h = Math.floor(lateMin / 60);
          const m = lateMin % 60;
          timeMsg = h > 0 ? `${h}h ${m}m late` : `${m}m late`;
        } else {
          const earlyMin = Math.abs(diffMin);
          const h = Math.floor(earlyMin / 60);
          const m = earlyMin % 60;
          timeMsg = diffMin === 0 ? 'Right on time!' : (h > 0 ? `${h}h ${m}m early` : `${m}m early`);
        }

        toast.info(
          `Checked in — ${timeMsg}. Your check-in is how your line manager and HR track attendance. Consistent punctuality reflects well on your record.`,
          { duration: 7000 }
        );
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to check in');
      }
    } catch (error) {
      console.error('Error checking in:', error);
    } finally {
      setLoading(false);
    }
  };

  const [showCheckoutWarning, setShowCheckoutWarning] = useState(false);

  const handleCheckOut = async () => {
    setLoading(true);
    try {
      // Check if timesheet is filled for today
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const today = new Date().toISOString().split('T')[0];
      const tsRes = await fetch(`${API_URL}/api/timesheets/my/${today}`, { headers });
      if (tsRes.ok) {
        const ts = await tsRes.json();
        if (!ts.id || !ts.entries || ts.entries.length === 0 || ts.total_minutes === 0) {
          setLoading(false);
          setShowCheckoutWarning(true);
          return;
        }
      }
    } catch (e) { /* don't block checkout */ }

    await performCheckOut();
  };

  const performCheckOut = async () => {
    setLoading(true);
    setShowCheckoutWarning(false);
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/attendance/check-out`, {
        method: 'POST',
        headers,
        body: JSON.stringify({})
      });
      if (response.ok) {
        toast.success('Checked out successfully. See you tomorrow!');
        setTimeout(() => { logout(); }, 1500);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to check out');
      }
    } catch (error) {
      console.error('Error checking out:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const handleSwitchView = () => {
    const newView = activeView === 'employee' ? 'admin' : 'employee';
    switchView(newView);
    navigate('/overview');
    toast.success(`Switched to ${newView === 'admin' ? 'Admin' : 'Employee'} view`);
  };

  const renderAttendanceButton = () => {
    if (!attendanceStatus || isSuperAdmin()) return null;

    const { status, check_in_time, total_hours, timezone } = attendanceStatus;

    if (status === 'not_checked_in') {
      return (
        <Button
          onClick={handleCheckIn}
          disabled={loading}
          className="bg-green-600 hover:bg-green-700 text-white"
          data-testid="check-in-button"
        >
          <LogIn className="w-4 h-4 mr-2" />
          Check In
        </Button>
      );
    }

    if (status === 'checked_in') {
      return (
        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-green-50 dark:bg-green-950 rounded-lg">
            <Clock className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium text-green-900 dark:text-green-100">
              In: {check_in_time} {timezone && <span className="text-xs text-green-600">({timezone})</span>}
            </span>
          </div>
          <Button
            onClick={handleCheckOut}
            disabled={loading}
            variant="outline"
            className="border-red-300 text-red-600 hover:bg-red-50"
            data-testid="check-out-button"
          >
            <LogOutIcon className="w-4 h-4 mr-2" />
            Check Out
          </Button>
        </div>
      );
    }

    if (status === 'checked_out') {
      return (
        <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg">
          <Clock className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Today: {total_hours}
          </span>
        </div>
      );
    }

    return null;
  };

  return (
    <>
    <div className="sticky top-0 z-40 backdrop-blur-sm bg-background/85 border-b border-border" data-testid="topbar">
      <div className="flex items-center justify-between h-16 px-4 sm:px-6">
        {/* Left side - Ping icon + online team members */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/ping')}
            className="relative flex items-center justify-center hover:scale-105 transition-transform"
            data-testid="ping-header-button"
          >
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#25D366' }}>
              <MessageCircle className="w-7 h-7 text-white" fill="white" strokeWidth={0} />
            </div>
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[22px] h-[22px] bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold px-1 shadow-md border-2 border-white">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>

          {/* Online team members (same office, active login session) */}
          {onlineTeam.length > 0 && (() => {
            const VISIBLE_LIMIT = 5;
            const visible = onlineTeam.slice(0, VISIBLE_LIMIT);
            const overflow = Math.max(0, onlineTeam.length - VISIBLE_LIMIT);
            const firstName = (m) => (m.name || '').split(' ')[0] || m.name || 'Teammate';
            const startChat = (m) => {
              setShowOnlinePopover(false);
              const [first, ...rest] = (m.name || '').split(' ');
              navigate('/ping', {
                state: {
                  openChatWith: {
                    id: m.user_id,
                    user_id: m.user_id,
                    first_name: first || m.name,
                    last_name: rest.join(' ') || '',
                  },
                },
              });
            };
            const Avatar = ({ m, size = 'w-9 h-9', textSize = 'text-xs' }) => (
              <div className={`relative ${size} shrink-0`}>
                <div className={`w-full h-full rounded-full bg-blue-100 overflow-hidden flex items-center justify-center ${textSize} font-bold text-blue-800`}>
                  {m.profile_picture ? (
                    <img
                      src={m.profile_picture.startsWith('data:') || m.profile_picture.startsWith('http')
                        ? m.profile_picture
                        : `data:image/jpeg;base64,${m.profile_picture}`}
                      alt={m.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    m.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
                  )}
                </div>
                {/* Green online dot — kept outside the clipped wrapper so it isn't truncated */}
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-background ring-1 ring-emerald-500/30 shadow-sm" />
              </div>
            );
            return (
              <div className="relative flex items-center" ref={onlinePopoverRef}>
                <div className="flex -space-x-2">
                  {visible.map((m) => (
                    <button
                      key={m.user_id}
                      type="button"
                      onClick={() => startChat(m)}
                      title={firstName(m)}
                      data-testid={`online-avatar-${m.user_id}`}
                      className="relative rounded-full border-2 border-background hover:scale-110 hover:z-10 transition-transform focus:outline-none focus:ring-2 focus:ring-primary focus:z-10"
                    >
                      <Avatar m={m} />
                    </button>
                  ))}
                </div>
                {overflow > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowOnlinePopover((v) => !v)}
                    className="ml-1 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 text-xs font-semibold transition-colors"
                    data-testid="online-team-more"
                    title={`Show ${overflow} more online`}
                  >
                    +{overflow} more
                  </button>
                )}
                {overflow === 0 && (
                  <button
                    type="button"
                    onClick={() => setShowOnlinePopover((v) => !v)}
                    className={`ml-1 inline-flex items-center justify-center w-6 h-6 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-all ${showOnlinePopover ? 'rotate-180' : ''}`}
                    data-testid="online-team-trigger"
                    title="See all teammates online"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                )}
                {showOnlinePopover && (
                  <div
                    className="absolute left-0 top-full mt-2 w-80 bg-popover text-popover-foreground border rounded-xl shadow-2xl z-50 overflow-hidden"
                    data-testid="online-team-popover"
                  >
                    <div className="px-4 py-3 border-b bg-gradient-to-r from-emerald-50 to-blue-50 dark:from-emerald-950/40 dark:to-blue-950/40">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-semibold flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                            Active now
                          </div>
                          <div className="text-[11px] text-muted-foreground mt-0.5">
                            {onlineTeam.length} {onlineTeam.length === 1 ? 'teammate' : 'teammates'} online
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="max-h-80 overflow-y-auto divide-y">
                      {onlineTeam.map((m) => (
                        <div
                          key={m.user_id}
                          className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/30 transition-colors"
                          data-testid={`online-team-row-${m.user_id}`}
                        >
                          <Avatar m={m} size="w-10 h-10" textSize="text-sm" />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">{m.name}</div>
                            <div className="text-[11px] text-emerald-600 font-medium flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                              Active now
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => startChat(m)}
                            className="gap-1.5 h-8 shrink-0"
                            data-testid={`chat-with-${m.user_id}`}
                            title={`Chat with ${firstName(m)}`}
                          >
                            <MessageCircle className="h-3.5 w-3.5" />
                            Chat
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        <div className="flex items-center gap-3">
          {/* Check In/Out Button */}
          {renderAttendanceButton()}

          {/* View Switch & Indicator for Office Admins */}
          {canSwitchView() && (
            <div className="flex items-center gap-1">
              {/* Current View Icon Indicator */}
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  activeView === 'employee'
                    ? 'bg-green-100 dark:bg-green-900'
                    : 'bg-purple-100 dark:bg-purple-900'
                }`}
                title={activeView === 'employee' ? 'Employee View' : 'Admin View'}
              >
                {activeView === 'employee' ? (
                  <Users className="w-4 h-4 text-green-600 dark:text-green-400" />
                ) : (
                  <Shield className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                )}
              </div>
              
              {/* Switch Icon Button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleSwitchView}
                className="hover:bg-gray-100 dark:hover:bg-gray-800"
                data-testid="switch-view-button"
                title={activeView === 'employee' ? 'Switch to Admin View' : 'Switch to Employee View'}
              >
                <ArrowLeftRight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </Button>
            </div>
          )}

          {/* Office Name Badge */}
          {currentOffice && (
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-950 rounded-lg">
              <Building className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {currentOffice.name}
              </span>
            </div>
          )}
          {isSuperAdmin() && !currentOffice && (
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-green-50 dark:bg-green-950 rounded-lg">
              <Shield className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-900 dark:text-green-100">
                Global Access
              </span>
            </div>
          )}

          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={onThemeToggle}
            data-testid="theme-toggle"
          >
            {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
          </Button>

          {/* Notifications */}
          <NotificationCenter />

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 px-3" data-testid="user-menu-trigger">
                <div className="flex items-center gap-2">
                  <div className="hidden md:block text-right">
                    <p className="text-sm font-medium">{currentUser?.full_name}</p>
                    {isSuperAdmin() && (
                      <p className="text-xs text-muted-foreground">Super Admin</p>
                    )}
                  </div>
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {currentUser?.first_name?.[0]}{currentUser?.last_name?.[0]}
                    </span>
                  </div>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 bg-background" data-testid="user-menu-content">
              <div className="px-3 py-2">
                <p className="text-sm font-medium">{currentUser?.full_name}</p>
                <p className="text-xs text-muted-foreground">{currentUser?.phone}</p>
                {isSuperAdmin() && (
                  <Badge variant="secondary" className="text-xs mt-1">
                    <Shield className="w-3 h-3 mr-1" />
                    Super Admin
                  </Badge>
                )}
              </div>
              
              <DropdownMenuSeparator />
              
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item" className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>

    {/* Checkout Warning Modal */}
    {showCheckoutWarning && (
      <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center" data-testid="checkout-warning-modal">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
            <Clock className="h-7 w-7 text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Timesheet Not Filled</h2>
          <p className="text-sm text-gray-600 mb-6 leading-relaxed">
            You haven't logged your timesheet for today. Your timesheet is how your line manager and HR track your daily activities.
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => { setShowCheckoutWarning(false); navigate('/my-timesheet'); }}
              data-testid="go-to-timesheet-btn">
              Go to Timesheet
            </Button>
            <Button variant="destructive" onClick={performCheckOut}
              data-testid="checkout-anyway-btn">
              Check Out Anyway
            </Button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default TopBar;
